import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';
import { FontIcon } from 'react-md';

import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { useTheme } from '../../components/Theme/ThemeContext';

export const TabsHeader = styled(Tabs)`
    div {
        flex: 1;
        height: ${props => (props.topNavigationTabs ? '56px' : '48px')};

        button {
            min-width: ${props => (props.unsetMaxWidth ? 'unset' : '100%')} !important;
            height: ${props => (props.topNavigationTabs ? '48px' : '40px')};
            min-height: ${props => (props.topNavigationTabs ? '48px' : '40px')};
            width: ${props => (props.isSmallTab ? '80%' : '100%')} !important;
            margin: 4px 0;
            position: sticky;
            top: 0;
            z-index: 1;

            span:first-of-type {
                flex-direction: row !important;

                & > div {
                    width: fit-content;
                    flex: unset;
                    display: flex;
                    align-items: center;
                }
            }
        }
    }
`;

export const TabsContainer = styled(Paper)`
    width: 100%;
    height: 100%;
    overflow: ${props => (props.isSidePanelTabs ? '' : 'hidden')};
    box-shadow: unset !important;
    display: flex;
    flex-direction: column;

    .MuiTabs-root {
        position: sticky;
        top: 0;
        background: #fff;
        z-index: 1;
        ${props =>
            props.isSidePanelTabs &&
            `
            border-bottom: 1px solid #DDDDDD;
        `};
        ${props =>
            props.applyShadow &&
            props.isSidePanelTabs &&
            `
            box-shadow: 0px 0px 1px rgb(0 0 0 / 15%), 0px 2px 8px rgb(0 0 0 / 5%), 0px 2px 3px rgb(0 0 0 / 10%);
        `};
    }

    @keyframes expandRight {
        0% {
            transform: translateX(180px);
            opacity: 0;
        }
        100% {
            transform: translateX(0px);
            opacity: 1;
        }
    }
    @keyframes expandLeft {
        0% {
            transform: translateX(-280px);
            opacity: 0;
        }
        100% {
            transform: translateX(0);
            opacity: 1;
        }
    }

    & > div:nth-of-type(2) {
        height: ${props => props.containerHeight};
        overflow: scroll;
        animation: ${props =>
            (props.animationDirection === 'left' && `expandRight .3s ease-in forwards`) ||
            (props.animationDirection === 'right' && 'expandLeft .3s ease-out forwards')};
        transition: all cubic-bezier(0.1, 0.7, 1, 0.1) 300ms;
    }

    .MuiTabs-indicator {
        height: 3px !important;
    }
`;

export const TabElement = styled(Tab)`
    font-family: 'Roboto', sans-serif;
    font-size: 15px !important;
    font-weight: 500 !important;
    color: rgba(0, 0, 0, 0.6) !important;
    cursor: pointer;
    transition: opacity 0.1s;
    opacity: unset !important;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    text-transform: none !important;
    width: ${props => (props.isSmallTab ? '80%' : '100%')} !important;
    border-radius: 8px !important;

    ${props =>
        props.isSmallTab &&
        `
    &:first-of-type {
      margin-left: 16px !important;
    }`}
    &:not(&.active):hover {
        background: ${props => props.color + '10' || '#00a933' + '10'};
        color: ${props => props.color || '#00a933'} !important;
        width: ${props => (props.isSmallTab ? '80%' : '100%')} !important;
    }

    &:active {
        color: ${props => props.color || '#00a933'} !important;
        background: ${props => props.color + '30' || '#00a933' + '30'} !important;
    }

    &.active {
        color: ${props => props.color || '#00a933'} !important;
        font-weight: bold;
    }
`;

const StyledChip = styled.span`
    text-align: center;
    font-family: 'Cabin', sans-serif;
    font-size: 10px;
    color: #fff;
    background-color: ${props => props.primary};
    padding: 2px;
    width: 16px;
    height: 16px;
    border-radius: 10px;
    position: absolute;
    bottom: 24px;
    left: 39%;
    line-height: 12px;
`;

export const TabStyledFontIcon = styled(FontIcon)`
    margin: ${props => (props.displayIconWithoutLabel ? '0 auto' : '0 12px 0 0')} !important;
    color: inherit !important;
    ${props => props.topNavigationTabs && 'font-size: 24px !important'};
    font-size: 24px !important;
`;

const TabLabel = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    max-width: fit-content;
`;

const ContentWrapper = styled.div`
    flex: 1;
    position: relative;

    & > :first-child {
        top: 0;
        height: 100%;
    }
`;

const TabsComponent = ({
    data,
    isSmallTab,
    containerHeight,
    onMouseEnter,
    onMouseLeave,
    currentTab = 0,
    checkContentAllTabs,
    topNavigationTabs,
    isSidePanelTabs,
}) => {
    const [activeTab, setActiveTab] = useState(currentTab);
    const [previousActiveTab, setPreviousActiveTab] = useState(0);
    const [shadowContainer, setShadowContainer] = useState(false);
    const { theme } = useTheme();
    const onTabClick = value => {
        setPreviousActiveTab(activeTab);
        setActiveTab(value);
    };

    useEffect(() => {
        if (currentTab) {
            setActiveTab(currentTab);
        }
        if (data.length === 1) {
            setActiveTab(0);
        }
    }, [currentTab]);

    useEffect(() => {
        if (data.length === 1) {
            setActiveTab(0);
        }
    }, [data.length]);

    useEffect(() => {
        if (isSidePanelTabs) {
            const scrollable = document.getElementById('scrollable');

            if (scrollable) {
                scrollable.addEventListener('scroll', handleScrolling);
            }
            return () => {
                scrollable && scrollable.removeEventListener('scroll', handleScrolling);
            };
        }
    }, []);

    const handleScrolling = () => {
        const elem = document.getElementById('tabs-container');
        const scrollable = document.getElementById('scrollable');
        setShadowContainer(elem?.offsetTop <= scrollable?.scrollTop);
    };

    if (!data || data.length === 0) {
        return null;
    }

    return (
        <TabsContainer
            id={'tabs-container'}
            isSidePanelTabs={isSidePanelTabs}
            animationDirection={
                previousActiveTab !== activeTab &&
                (previousActiveTab < activeTab ? 'left' : 'right')
            }
            containerHeight={containerHeight}
            square
            onMouseEnter={it => {
                if (onMouseEnter) {
                    onMouseEnter(it);
                }
            }}
            onMouseLeave={it => {
                if (onMouseLeave) {
                    onMouseLeave(it);
                }
            }}
            applyShadow={shadowContainer}
        >
            <TabsHeader
                value={activeTab}
                indicatorColor="primary"
                textColor="primary"
                isSmallTab={isSmallTab}
                aria-label="disabled tabs example"
                topNavigationTabs={topNavigationTabs}
                unsetMaxWidth={data.length > 1}
                TabIndicatorProps={
                    isSmallTab
                        ? {
                              style: {
                                  width: `${80 / data.length}%`,
                                  marginLeft: '16px',
                                  borderRadius: '2px 2px 0px 0px',
                              },
                          }
                        : {
                              style: {
                                  borderRadius: '2px 2px 0px 0px',
                              },
                          }
                }
            >
                {data.map((tab, index) => (
                    <div key={`tab=${index}`}>
                        <TabElement
                            topNavigationTabs={topNavigationTabs}
                            className={activeTab === Number(index) && 'active'}
                            onClick={it => {
                                if (tab.onClick) {
                                    tab.onClick(it);
                                }
                                onTabClick(index);
                            }}
                            onMouseEnter={it => {
                                if (tab.onMouseEnter) {
                                    tab.onMouseEnter(it);
                                }
                            }}
                            onMouseLeave={it => {
                                if (tab.onMouseLeave) {
                                    tab.onMouseLeave(it);
                                }
                            }}
                            key={`tab-element-${index}`}
                            isSmallTab={isSmallTab}
                            color={theme.primary}
                            label={
                                <TabLabel>
                                    {tab.label}
                                    {tab.chip && (
                                        <StyledChip primary={theme.contrast}>{tab.chip}</StyledChip>
                                    )}
                                </TabLabel>
                            }
                            disableTouchRipple
                            iconPosition="start"
                            icon={
                                tab.labelIcon && (
                                    <TabStyledFontIcon
                                        iconClassName={
                                            activeTab === Number(index)
                                                ? 'material-icons'
                                                : 'material-icons-outlined'
                                        }
                                        displayIconWithoutLabel={!tab.label}
                                        topNavigationTabs={topNavigationTabs}
                                    >
                                        {tab.labelIcon}
                                    </TabStyledFontIcon>
                                )
                            }
                        />
                    </div>
                ))}
            </TabsHeader>
            <ContentWrapper>
                <ContentComponent
                    index={activeTab}
                    data={data}
                    checkContentAllTabs={checkContentAllTabs}
                />
            </ContentWrapper>
        </TabsContainer>
    );
};

const ContentComponent = React.memo(
    ({ data, index }) => {
        const tab = data[index];

        if (!tab) {
            return null;
        }

        const Content = tab.content;

        if (typeof Content === 'function') {
            return <Content />;
        }

        if (typeof Content === 'object') {
            return Content;
        }
        return null;
    },
    (prevProps, nextProps) => {
        const chatPrevProps = prevProps.data.find(item => item.id === 'user-interaction-chat');
        const chatNextProps = nextProps.data.find(item => item.id === 'user-interaction-chat');
        const qaPrevProps = prevProps.data.find(item => item.id === 'user-interaction-qa');
        const qaNextProps = nextProps.data.find(item => item.id === 'user-interaction-qa');
        const attendeesPrevProps = prevProps.data.find(
            item => item.id === 'user-interaction-attendees',
        );
        const attendeesNextProps = nextProps.data.find(
            item => item.id === 'user-interaction-attendees',
        );
        const isChatChipModified =
            chatPrevProps && chatNextProps && chatPrevProps.chip !== chatNextProps.chip;
        const isQaChipModified =
            qaPrevProps && qaNextProps && qaPrevProps.chip !== qaNextProps.chip;
        const isAttendeeLabelModified = attendeesPrevProps?.label !== attendeesNextProps?.label;
        const shouldNotRerender = isChatChipModified || isQaChipModified || isAttendeeLabelModified;

        // We should always rerender when we are switching tabs
        if (prevProps.index !== nextProps.index) {
            return false;
        }
        if (shouldNotRerender) {
            return true;
        }
        if (
            nextProps.data.length === 1 &&
            nextProps.data[0].checkContentOnly &&
            prevProps.data[0].checkContentOnly
        ) {
            return (
                isEqual(
                    JSON.stringify(prevProps.data.content),
                    JSON.stringify(nextProps.data.content),
                ) &&
                prevProps.data.length === nextProps.data.length &&
                prevProps.index === nextProps.index
            );
        }

        if (nextProps.checkContentAllTabs) {
            return (
                isEqual(
                    JSON.stringify(prevProps.data.content),
                    JSON.stringify(nextProps.data.content),
                ) &&
                prevProps.data.length === nextProps.data.length &&
                prevProps.index === nextProps.index
            );
        }
        return (
            isEqual(JSON.stringify(prevProps.data), JSON.stringify(nextProps.data)) &&
            prevProps.data.length === nextProps.data.length &&
            prevProps.index === nextProps.index
        );
    },
);

const TabsWithMemo = React.memo(
    props => {
        return <TabsComponent {...props} />;
    },
    (prevProps, nextProps) => {
        return isEqual(JSON.stringify(prevProps.data), JSON.stringify(nextProps.data));
    },
);

const TabsContiner = props => {
    if (props.useMemo) {
        return <TabsWithMemo {...props} />;
    } else {
        return <TabsComponent {...props} />;
    }
};

export default TabsContiner;
